import { CollectionAttribute, useCollectionAttributes } from '@/hooks/use-collection-attributes';
import { isNumber } from '@onbeam/utils';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';

export const getRange = (start: number, end: number) =>
  Array.from({ length: end - start + 1 }, (_, index) => start + index);

export type AttributeValue = CollectionAttribute & {
  isSelected: boolean;
};

export enum AttributeType {
  RANGE = 'range',
  SELECTION = 'selection',
  NUMBER = 'number',
  DATE = 'date',
}

export type Attribute = {
  key: string;
  type: AttributeType;
  values: AttributeValue[];
};

export const useFacets = (contractAddress: string) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { data, isInitialLoading } = useCollectionAttributes(contractAddress);

  const attributes = (data?.attributes || [])
    // NOTE: date attributes are not supported yet
    .filter(({ kind }) => kind !== AttributeType.DATE)
    .map(({ key, values, kind, minRange, maxRange }) => {
      if (kind === AttributeType.RANGE && isNumber(minRange) && isNumber(maxRange)) {
        const selectedParams = searchParams.getAll(`attributes[${key}]`);
        const min = Number(selectedParams[0]);
        const max = Number(selectedParams[1] || selectedParams[0]);
        const selectedRange: number[] = [];
        if (!isNaN(min)) selectedRange.push(min);
        if (!isNaN(max)) selectedRange.push(max);
        const attribute: Attribute = {
          key,
          type: AttributeType.RANGE,
          values: [minRange, ...selectedRange, maxRange].map(value => {
            return {
              value: String(value),
              isSelected: selectedRange.includes(value),
            };
          }),
        };
        return attribute;
      }

      if (kind === AttributeType.NUMBER && isNumber(minRange) && isNumber(maxRange)) {
        const attribute: Attribute = {
          key,
          type: AttributeType.SELECTION,
          values: getRange(minRange, maxRange).map(value => ({
            value: String(value),
            isSelected: searchParams.getAll(`attributes[${key}]`).includes(String(value)),
          })),
        };
        return attribute;
      }

      const attribute: Attribute = {
        key,
        type: AttributeType.SELECTION,
        values:
          values?.map(value => ({
            ...value,
            isSelected: searchParams.getAll(`attributes[${key}]`).includes(value.value),
          })) || [],
      };
      return attribute;
    })
    .sort((a, b) => a.key.localeCompare(b.key));

  const selectedAttributes = attributes
    .filter(attribute => attribute.values.some(value => value.isSelected))
    .reduce((acc, curr) => {
      if (curr.type === AttributeType.RANGE) {
        const filterSelected = curr.values.filter(value => value.isSelected);
        if (!filterSelected.length) return acc;
        const minRange = curr.values.find(value => value.isSelected);
        const maxRange = curr.values.findLast(value => value.isSelected);

        const minValue = minRange?.value || '0';
        const maxValue = maxRange?.value || '0';

        acc[`range_attributes[${curr.key}]`] = `${minValue},${maxValue}`;
      }

      if (curr.type === AttributeType.SELECTION) {
        acc[`attributes[${curr.key}]`] = curr.values
          .filter(value => value.isSelected)
          .map(value => value.value);
      }

      return acc;
    }, {} as Record<string, string[] | string>);

  const from = searchParams.get('from');
  const to = searchParams.get('to');
  const maxRarityRank = searchParams.get('maxRarityRank');
  const tokenName = searchParams.get('q');

  const onClearFilters = () => {
    const { contract, chain, tab } = router.query;

    // Make sure the tab param is not removed when the filter is cleared
    const queryTab = tab ? { tab } : {};
    const newQueryParams = { contract, chain, ...queryTab };

    router.replace({ query: newQueryParams }, undefined, {
      shallow: true,
    });
  };

  return {
    attributes,
    selectedAttributes,
    minFloorAskPrice: from ? Number(from) : undefined,
    maxFloorAskPrice: to ? Number(to) : undefined,
    maxRarityRank: maxRarityRank ? Number(maxRarityRank) : undefined,
    tokenName: tokenName || undefined,
    onClearFilters,
    isInitialLoading: isInitialLoading || !router.isReady,
  };
};
